import type { ReactNode } from 'react';
import { type SxProps, type Theme } from '@mui/material/styles';
import { Box, Typography } from '@/primitives';
import { getGuideLabelTransform } from './guideLabel';
import { getGuidePosition } from './ebvPercentileUtils';
import type { ChartAxisBandSideWidth } from './ebvPercentileTypes';

/** Left/right gutters match each data row + `PercentileScaleRow` so headings align with the chart band (50th %ile centre). */
export function ChartAxisBandAlign({
  traitColumnWidth,
  leftDirectionColumnWidth,
  rightDirectionColumnWidth,
  children,
  sx,
  /** Extra width on `md+` past the right-direction column (e.g. Perc 40, Acc 45). */
  rightExtensionMdPx = 40,
  /** When set, the centre band matches the fixed chart axis width (see `EBV_CHART_AXIS_WIDTH_PX`). */
  trackWidthPx,
  /**
   * When set, width of the spacer before the track (e.g. potential-change: trait + left + 50px range + grid gaps).
   * Defaults to `traitColumnWidth + leftDirectionColumnWidth` (percentile layout).
   */
  leftBeforeTrackWidthPx,
  /**
   * When set, width of the spacer after the track. Defaults to `{ xs: right, md: right + rightExtensionMdPx }`.
   */
  rightAfterTrackWidthPx,
}: {
  traitColumnWidth: number;
  leftDirectionColumnWidth: number;
  rightDirectionColumnWidth: number;
  children: ReactNode;
  sx?: SxProps<Theme>;
  rightExtensionMdPx?: number;
  trackWidthPx?: number;
  leftBeforeTrackWidthPx?: ChartAxisBandSideWidth;
  rightAfterTrackWidthPx?: ChartAxisBandSideWidth;
}) {
  const leftGutterDefault = traitColumnWidth + leftDirectionColumnWidth;
  const leftSpacerSx = (() => {
    if (leftBeforeTrackWidthPx === undefined) {
      return { width: leftGutterDefault };
    }
    if (typeof leftBeforeTrackWidthPx === 'number') {
      return { width: leftBeforeTrackWidthPx };
    }
    return {
      width: {
        xs: leftBeforeTrackWidthPx.xs,
        md: leftBeforeTrackWidthPx.md,
      },
    };
  })();

  const rightSpacerDefaultMd = rightDirectionColumnWidth + rightExtensionMdPx;
  const rightSpacerSx = (() => {
    if (rightAfterTrackWidthPx === undefined) {
      return {
        width: {
          xs: rightDirectionColumnWidth,
          md: rightSpacerDefaultMd,
        },
      };
    }
    if (typeof rightAfterTrackWidthPx === 'number') {
      return { width: rightAfterTrackWidthPx };
    }
    return {
      width: {
        xs: rightAfterTrackWidthPx.xs,
        md: rightAfterTrackWidthPx.md,
      },
    };
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        // Match data row `px: 1` so title/footer/description align with the chart axis (not shifted vs tick labels).
        px: 1,
        boxSizing: 'border-box',
        ...sx,
      }}
    >
      <Box sx={{ flexShrink: 0, ...leftSpacerSx }} aria-hidden />
      <Box
        sx={
          trackWidthPx != null
            ? {
                width: trackWidthPx,
                maxWidth: trackWidthPx,
                minWidth: 0,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }
            : {
                flex: 1,
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }
        }
      >
        {children}
      </Box>
      <Box sx={{ flexShrink: 0, ...rightSpacerSx }} aria-hidden />
    </Box>
  );
}

/** Public animal detail "EBV Percentile Graph": trait percentiles vs breed (horizontal bars). */
export function PercentileScaleRow({
  variant,
  percentileScale,
  traitColumnWidth,
  leftDirectionColumnWidth,
  rightDirectionColumnWidth,
  axisWidthPx,
  useFlexAxis,
}: {
  variant: 'header' | 'footer';
  percentileScale: readonly number[];
  traitColumnWidth: number;
  leftDirectionColumnWidth: number;
  rightDirectionColumnWidth: number;
  axisWidthPx: number;
  /** On mobile: make the axis flex to fill available space instead of using fixed axisWidthPx. */
  useFlexAxis?: boolean;
}) {
  const tickRowHeight = 16;
  const borderSx =
    variant === 'header'
      ? {
          mb: 0.5,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }
      : {
          mt: 1,
          pt: 1,
          borderTop: 1,
          borderColor: 'divider',
        };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        boxSizing: 'border-box',
        // Match data rows so scale ticks (100…1) sit over the same 500px band as the bars.
        px: { xs: 0, md: 1 },
        ...borderSx,
      }}
    >
      <Box
        sx={{
          width: traitColumnWidth + leftDirectionColumnWidth,
          flexShrink: 0,
          height: tickRowHeight,
        }}
      />
      <Box
        sx={
          useFlexAxis
            ? {
                flex: 1,
                width: 'auto',
                maxWidth: 'none',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
              }
            : {
                width: axisWidthPx,
                maxWidth: axisWidthPx,
                minWidth: 0,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
              }
        }
      >
        <Box
          sx={{
            position: 'relative',
            height: tickRowHeight,
            flexShrink: 0,
            width: '100%',
          }}
        >
          {percentileScale.map((num) => (
            <Typography
              key={num}
              variant="bodysmall"
              sx={{
                position: 'absolute',
                left: getGuidePosition(num),
                top: 0,
                transform: getGuideLabelTransform(num),
                fontSize: 10,
                fontWeight: 500,
                color: 'text.disabled',
                fontFamily: 'monospace',
                lineHeight: 1,
              }}
            >
              {num}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          width: rightDirectionColumnWidth,
          flexShrink: 0,
          height: tickRowHeight,
          display: { xs: 'none', md: 'block' },
        }}
      />
      <Box
        sx={{
          width: 40,
          flexShrink: 0,
          height: tickRowHeight,
          textAlign: 'right',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Typography
          variant="bodysmall"
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Perc
        </Typography>
      </Box>
    </Box>
  );
}
